import { z } from 'zod';
import { IMPORT_FORMAT, IMPORT_VERSION, DEFAULT_COURSE } from '@/constants/app';

/**
 * Strict validation of an import block. Failures carry the exact path and
 * reason, e.g. `questions[7].answerIndex must be between 0 and 3`.
 * Unknown fields are a warning, never a failure.
 */

export interface ImportIssue {
  path: string;
  message: string;
}

const CONTROL_CHARS = /[ --]/;

const idField = z
  .string()
  .min(1, 'is required')
  .max(200, 'must be 200 characters or fewer')
  .refine((s) => s.trim() === s, { message: 'must not start or end with whitespace' })
  .refine((s) => !CONTROL_CHARS.test(s), { message: 'must not contain control characters' });

const text = (max = 20000) =>
  z
    .string()
    .min(1, 'must not be empty')
    .max(max, `must be ${max} characters or fewer`)
    .refine((s) => !CONTROL_CHARS.test(s), { message: 'must not contain control characters' });

const optionalText = (max = 20000) =>
  z
    .string()
    .max(max)
    .refine((s) => !CONTROL_CHARS.test(s), { message: 'must not contain control characters' })
    .optional();

const difficulty = z
  .union([z.literal(1), z.literal(2), z.literal(3)], {
    errorMap: () => ({ message: 'must be 1, 2 or 3' }),
  })
  .default(2);

const flag = z.enum(['filled', 'check', 'priority'], {
  errorMap: () => ({ message: 'must be "filled", "check" or "priority"' }),
});

/**
 * What a full-credit answer includes. It is shown beside the model answer and
 * never scored: the grading is still his own Got it / Missed it.
 */
const rubric = z
  .array(text(1000))
  .min(1, 'must have at least one point')
  .max(10, 'must have at most 10 points')
  .optional();

export const flashcardSchema = z
  .object({
    id: idField,
    category: text(120),
    difficulty,
    front: text(),
    back: text(),
    flag: flag.optional(),
    flagText: optionalText(2000),
  })
  .passthrough();

const mcSchema = z
  .object({
    id: idField,
    type: z.literal('mc'),
    category: text(120),
    difficulty,
    q: text(),
    opts: z
      .array(text())
      .min(2, 'must have at least 2 options')
      .max(6, 'must have at most 6 options'),
    answerIndex: z.number().int('must be a whole number').nonnegative('must be 0 or greater'),
    sample: optionalText(),
    rubric,
    note: optionalText(),
    flag: flag.optional(),
    flagText: optionalText(2000),
  })
  .passthrough();

const shortSchema = z
  .object({
    id: idField,
    type: z.literal('short'),
    category: text(120),
    difficulty,
    q: text(),
    sample: text(),
    rubric,
    note: optionalText(),
    flag: flag.optional(),
    flagText: optionalText(2000),
  })
  .passthrough();

export const questionPartSchema = z
  .object({
    label: text(20),
    prompt: text(),
    sample: text(),
    rubric,
  })
  .passthrough();

/** A longer written question: a shared setup in `q`, then labelled parts. */
const freeSchema = z
  .object({
    id: idField,
    type: z.literal('free'),
    category: text(120),
    difficulty,
    q: text(),
    parts: z
      .array(questionPartSchema)
      .min(1, 'must have at least 1 part')
      .max(6, 'must have at most 6 parts'),
    rubric,
    note: optionalText(),
    flag: flag.optional(),
    flagText: optionalText(2000),
  })
  .passthrough();

/** A numeric problem he works by hand, with the solution shown step by step. */
const workSchema = z
  .object({
    id: idField,
    type: z.literal('work'),
    category: text(120),
    difficulty,
    q: text(),
    steps: z.array(text()).min(1, 'must have at least one step'),
    answer: text(500),
    rubric,
    note: optionalText(),
    flag: flag.optional(),
    flagText: optionalText(2000),
  })
  .passthrough();

export const questionSchema = z.discriminatedUnion(
  'type',
  [mcSchema, shortSchema, freeSchema, workSchema],
  {
    errorMap: (issue, ctx) => {
      if (issue.code === z.ZodIssueCode.invalid_union_discriminator) {
        return { message: 'type must be "mc", "short", "free" or "work"' };
      }
      return { message: ctx.defaultError };
    },
  },
);

/**
 * Cross-field checks a discriminated-union member cannot make (a refinement
 * stops it being a plain object schema): `answerIndex` against the length of
 * `opts`, and part labels against each other. Checking at the array level keeps
 * the reported path exact, e.g. `questions[7].answerIndex`.
 */
export const questionsArraySchema = z
  .array(questionSchema)
  .default([])
  .superRefine((questions, ctx) => {
    questions.forEach((q, i) => {
      if (q.type === 'mc' && q.answerIndex >= q.opts.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'answerIndex'],
          message: `must be between 0 and ${q.opts.length - 1}`,
        });
      }
      if (q.type === 'free') {
        const seen = new Set<string>();
        q.parts.forEach((part, j) => {
          if (seen.has(part.label)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'parts', j, 'label'],
              message: `"${part.label}" is used by an earlier part; labels must be unique`,
            });
          }
          seen.add(part.label);
        });
      }
    });
  });

// --------------------------------------------------------- problem templates
// Stage 1 validates shape only. Semantic checks (does the expression evaluate,
// are all variables defined) arrive with the expression parser in Stage 6.

const variableSpecSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('float'),
    min: z.number(),
    max: z.number(),
    sigfigs: z.number().int().min(1).max(10),
  }),
  z.object({ type: z.literal('int'), min: z.number().int(), max: z.number().int() }),
  z.object({
    type: z.literal('choice'),
    values: z.array(z.union([z.string(), z.number()])).min(1),
  }),
  z.object({
    type: z.literal('choiceSet'),
    fields: z.array(text(80)).min(1),
    values: z.array(z.record(z.union([z.string(), z.number()]))).min(1),
  }),
]);

export const templateSchema = z
  .object({
    id: idField,
    category: text(120),
    difficulty,
    prompt: text(),
    variables: z.record(variableSpecSchema),
    constraints: z.array(text(500)).default([]),
    compute: text(2000),
    answerUnits: z.string().max(40),
    unitAliases: z.array(z.string().max(40)).default([]),
    sigfigRule: z.union([
      z.enum(['min-of-inputs', 'decimal-places', 'log-mantissa']),
      z.number().int().min(1).max(10),
    ]),
    tolerance: z
      .object({ relative: z.number().positive().optional(), absolute: z.number().positive().optional() })
      .default({ relative: 0.01 }),
    steps: z.array(text()).default([]),
  })
  .passthrough();

export const formulaSchema = z
  .object({
    id: idField,
    name: text(200),
    expr: text(500),
    topic: text(120),
    notes: optionalText(2000),
  })
  .passthrough();

export const constantSchema = z
  .object({
    id: idField,
    name: text(200),
    symbol: text(40),
    value: text(80),
    units: optionalText(40),
  })
  .passthrough();

// ------------------------------------------------------------------- session

export const fullSessionSchema = z
  .object({
    id: idField,
    kind: z.enum(['class', 'chapter', 'textbook'], {
      errorMap: () => ({ message: 'must be "class", "chapter" or "textbook"' }),
    }),
    number: z.number().int().nullable().optional().default(null),
    title: text(300),
    shortLabel: text(60),
    addedAt: z
      .string()
      .refine((s) => !Number.isNaN(Date.parse(s)), { message: 'must be an ISO 8601 date-time' }),
  })
  .passthrough();

export const partialSessionSchema = z.object({ id: idField }).passthrough();

const partSchema = z
  .object({
    index: z.number().int().min(1, 'must be 1 or greater'),
    of: z.number().int().min(1, 'must be 1 or greater'),
  })
  .refine((p) => p.index <= p.of, { message: 'index must not exceed of' })
  .default({ index: 1, of: 1 });

/** Envelope fields shared by first and later parts. */
const envelope = {
  format: z.literal(IMPORT_FORMAT, {
    errorMap: () => ({ message: `must be "${IMPORT_FORMAT}"` }),
  }),
  version: z.literal(IMPORT_VERSION, {
    errorMap: () => ({ message: `must be ${IMPORT_VERSION}` }),
  }),
  course: z.string().min(1).max(40).default(DEFAULT_COURSE),
  part: partSchema,
  flashcards: z.array(flashcardSchema).default([]),
  questions: questionsArraySchema,
  problemTemplates: z.array(templateSchema).default([]),
  reference: z
    .object({
      formulas: z.array(formulaSchema).default([]),
      constants: z.array(constantSchema).default([]),
    })
    .default({ formulas: [], constants: [] }),
};

export const firstPartBlockSchema = z.object({ ...envelope, session: fullSessionSchema }).passthrough();
export const laterPartBlockSchema = z
  .object({ ...envelope, session: partialSessionSchema })
  .passthrough();

export type FlashcardInput = z.infer<typeof flashcardSchema>;
export type QuestionInput = z.infer<typeof questionSchema>;
export type TemplateInput = z.infer<typeof templateSchema>;
export type FormulaInput = z.infer<typeof formulaSchema>;
export type ConstantInput = z.infer<typeof constantSchema>;
export type FullSessionInput = z.infer<typeof fullSessionSchema>;

export interface ValidatedBlock {
  course: string;
  part: { index: number; of: number };
  /** Present only on part 1. Later parts carry just the id. */
  session: FullSessionInput | { id: string };
  sessionId: string;
  isFirstPart: boolean;
  flashcards: FlashcardInput[];
  questions: QuestionInput[];
  problemTemplates: TemplateInput[];
  formulas: FormulaInput[];
  constants: ConstantInput[];
  warnings: ImportIssue[];
}

export function formatPath(path: Array<string | number>): string {
  let out = '';
  for (const seg of path) {
    if (typeof seg === 'number') out += `[${seg}]`;
    else out += out ? `.${seg}` : seg;
  }
  return out || '(root)';
}

export function issuesFromZod(error: z.ZodError): ImportIssue[] {
  return error.issues.map((i) => ({ path: formatPath(i.path), message: i.message }));
}

const KNOWN_TOP_LEVEL = new Set([
  'format',
  'version',
  'course',
  'part',
  'session',
  'flashcards',
  'questions',
  'problemTemplates',
  'reference',
]);
const KNOWN_SESSION = new Set(['id', 'kind', 'number', 'title', 'shortLabel', 'addedAt']);
const KNOWN_FLASHCARD = new Set([
  'id',
  'category',
  'difficulty',
  'front',
  'back',
  'flag',
  'flagText',
]);
const KNOWN_QUESTION = new Set([
  'id',
  'type',
  'category',
  'difficulty',
  'q',
  'opts',
  'answerIndex',
  'sample',
  'parts',
  'steps',
  'answer',
  'rubric',
  'note',
  'flag',
  'flagText',
]);
const KNOWN_QUESTION_PART = new Set(['label', 'prompt', 'sample', 'rubric']);
const KNOWN_TEMPLATE = new Set([
  'id',
  'category',
  'difficulty',
  'prompt',
  'variables',
  'constraints',
  'compute',
  'answerUnits',
  'unitAliases',
  'sigfigRule',
  'tolerance',
  'steps',
]);
const KNOWN_FORMULA = new Set(['id', 'name', 'expr', 'topic', 'notes']);
const KNOWN_CONSTANT = new Set(['id', 'name', 'symbol', 'value', 'units']);
const KNOWN_REFERENCE = new Set(['formulas', 'constants']);

function unknownKeys(
  obj: unknown,
  known: Set<string>,
  path: string,
  out: ImportIssue[],
): void {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
  for (const key of Object.keys(obj as Record<string, unknown>)) {
    if (!known.has(key)) {
      out.push({ path: path ? `${path}.${key}` : key, message: 'is not a known field and was ignored' });
    }
  }
}

/** Walks the raw block and reports unknown fields as warnings. */
export function collectWarnings(raw: unknown): ImportIssue[] {
  const out: ImportIssue[] = [];
  if (!raw || typeof raw !== 'object') return out;
  const block = raw as Record<string, unknown>;

  unknownKeys(block, KNOWN_TOP_LEVEL, '', out);
  unknownKeys(block.session, KNOWN_SESSION, 'session', out);
  unknownKeys(block.reference, KNOWN_REFERENCE, 'reference', out);

  const walk = (arr: unknown, known: Set<string>, base: string) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((entry, i) => unknownKeys(entry, known, `${base}[${i}]`, out));
  };

  walk(block.flashcards, KNOWN_FLASHCARD, 'flashcards');
  walk(block.questions, KNOWN_QUESTION, 'questions');
  if (Array.isArray(block.questions)) {
    block.questions.forEach((q, i) => {
      const question = q as Record<string, unknown> | null;
      walk(question?.parts, KNOWN_QUESTION_PART, `questions[${i}].parts`);

      // A multiple-choice question carries its reasoning only in `note`: there
      // is no sample, no worked steps and no parts to fall back on, so without
      // one the app has nothing to explain the answer with. A warning rather
      // than a failure — the question still works, it is just mute.
      const note = typeof question?.note === 'string' ? question.note.trim() : '';
      if (question?.type === 'mc' && !note) {
        out.push({
          path: `questions[${i}].note`,
          message: 'is missing, so this question cannot explain its answer',
        });
      }
    });
  }
  walk(block.problemTemplates, KNOWN_TEMPLATE, 'problemTemplates');
  const ref = block.reference as Record<string, unknown> | undefined;
  walk(ref?.formulas, KNOWN_FORMULA, 'reference.formulas');
  walk(ref?.constants, KNOWN_CONSTANT, 'reference.constants');

  return out;
}
